import React from 'react';
import { Link } from 'react-router-dom';
import { IconChevronRight, IconWarningDot } from './AdminIcons';
import './AdminUiPrimitives.css';

export interface BreadcrumbItem {
  label: string;
  to?: string;
  onClick?: () => void;
  isCurrent?: boolean;
  title?: string;
}

export const AdminBreadcrumbs: React.FC<{ items: BreadcrumbItem[] }> = ({ items }) => {
  return (
    <nav aria-label="Breadcrumb" className="aup-breadcrumb-nav">
      <ol className="aup-breadcrumb-list">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="aup-breadcrumb-item">
              {isLast || item.isCurrent ? (
                <span className="aup-breadcrumb-current" aria-current="page" title={item.title || item.label}>
                  {item.label}
                </span>
              ) : item.to ? (
                <Link to={item.to} className="aup-breadcrumb-link" title={item.title || item.label}>
                  {item.label}
                </Link>
              ) : (
                <button
                  type="button"
                  className="aup-breadcrumb-link aup-breadcrumb-button"
                  onClick={item.onClick}
                  title={item.title || item.label}
                >
                  {item.label}
                </button>
              )}
              {!isLast && <IconChevronRight size={14} className="aup-breadcrumb-sep" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export const UnsavedChangesIndicator: React.FC<{ dirty: boolean }> = ({ dirty }) => {
  if (!dirty) {
    return <span className="aup-clean-indicator">All changes saved</span>;
  }

  return (
    <span className="aup-dirty-indicator" role="status" aria-live="polite">
      <IconWarningDot size={8} className="aup-dirty-dot" />
      Unsaved changes
    </span>
  );
};

export const AdminSectionHeading: React.FC<{
  icon: React.ReactNode;
  title: string;
}> = ({ icon, title }) => (
  <h4 className="aup-section-heading">
    <span className="aup-section-icon" aria-hidden="true">{icon}</span>
    <span>{title}</span>
  </h4>
);

export const AdminStatCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  accent?: 'default' | 'active' | 'draft' | 'archived';
  onClick?: () => void;
  active?: boolean;
}> = ({ label, value, icon, accent = 'default', onClick, active }) => {
  const Comp = onClick ? 'button' : 'div';

  return (
    <Comp
      type={onClick ? 'button' : undefined}
      className={`aup-stat-card aup-stat-${accent} ${active ? 'is-active' : ''}`.trim()}
      onClick={onClick}
      aria-pressed={onClick ? active : undefined}
    >
      <div className="aup-stat-head">
        <span className="aup-stat-icon" aria-hidden="true">{icon}</span>
        <span className="aup-stat-label">{label}</span>
      </div>
      <div className="aup-stat-value">{value}</div>
    </Comp>
  );
};

export interface ConfirmDialogAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'primary' | 'destructive';
  disabled?: boolean;
}

export const ConfirmDialog: React.FC<{
  open: boolean;
  title: string;
  message: string;
  onClose: () => void;
  actions: ConfirmDialogAction[];
}> = ({ open, title, message, onClose, actions }) => {
  if (!open) {
    return null;
  }

  return (
    <div className="aup-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="aup-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aup-confirm-title"
        aria-describedby="aup-confirm-message"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="aup-confirm-title">{title}</h3>
        <p id="aup-confirm-message">{message}</p>
        <div className="aup-modal-actions">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={`aup-modal-btn aup-modal-btn-${action.variant || 'default'}`}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
